import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, TrendingUp, Wallet, MapPin, ShieldCheck, ClipboardList, Palette, Store, Briefcase, Settings } from "lucide-react";
import TopBar from "@/components/TopBar";
import CmsPanel from "@/components/CmsPanel";
import RubiposPanel from "@/components/admin/RubiposPanel";
import InvestorsPanel from "@/components/admin/InvestorsPanel";
import IntegrationsPanel from "@/components/admin/IntegrationsPanel";
import api from "@/lib/api";
import useSeo from "@/hooks/useSeo";
import { CRM } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";

const STAGES = [
  "Nuevo",
  "Contactado",
  "Calificando",
  "Reunión Agendada",
  "En Evaluación",
  "Formalización",
  "Participante",
  "Descartado",
];

const LABEL_STYLE = {
  calificado: "text-emerald-400 border-emerald-500/30 bg-emerald-900/10",
  a_revisar: "text-[#FFBF57] border-[#FFBF57]/40 bg-[#FFBF57]/[0.08]",
  no_calificado: "text-neutral-400 border-white/10 bg-white/[0.02]",
};

const LOC_STATUS = {
  prospectada: { label: "Prospectada", cls: "border-white/10 text-neutral-300" },
  negociacion: { label: "En negociación", cls: "border-[#FFBF57]/40 text-[#FFBF57]" },
  confirmada: { label: "Confirmada", cls: "border-emerald-500/40 text-emerald-400" },
};

const CAPITAL_FMT = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 });

export default function Dashboard() {
  useSeo({ title: "Panel de administración", description: "Administración de contenido y prospectos.", path: "/dashboard", noindex: true });
  const { user } = useAuth();
  const [tab, setTab] = useState("pipeline");
  const [leads, setLeads] = useState([]);
  const [summary, setSummary] = useState(null);
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [filterLabel, setFilterLabel] = useState("all");

  const canAdmin = user && (user.role === "admin" || user.role === "director");

  const loadData = async () => {
    try {
      const [l, s] = await Promise.all([api.get("/leads"), api.get("/leads/summary")]);
      setLeads(l.data);
      setSummary(s.data);
    } catch (e) {
      toast.error("No se pudieron cargar los leads");
    }
  };

  const loadLocations = async () => {
    try {
      const r = await api.get("/locations");
      setLocations(r.data);
    } catch (_e) {
      // silent
    }
  };

  const loadUsers = async () => {
    if (!canAdmin) return;
    try {
      const r = await api.get("/users");
      setUsers(r.data);
    } catch (_e) {
      // silent
    }
  };

  const loadAudit = async () => {
    if (!canAdmin) return;
    try {
      const r = await api.get("/audit-logs");
      setAudit(r.data);
    } catch (_e) {
      // silent
    }
  };

  useEffect(() => {
    loadData();
    loadLocations();
    loadUsers();
    loadAudit();
  }, [canAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const grouped = useMemo(() => {
    const g = Object.fromEntries(STAGES.map((s) => [s, []]));
    for (const l of leads) {
      if (filterLabel !== "all" && l.score_label !== filterLabel) continue;
      g[l.stage || "Nuevo"] = g[l.stage || "Nuevo"] || [];
      g[l.stage || "Nuevo"].push(l);
    }
    return g;
  }, [leads, filterLabel]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]">
      <TopBar variant="dashboard" />

      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div>
            <p className="eyebrow">Investor Journey</p>
            <h1 className="font-display text-4xl lg:text-5xl mt-3">Pipeline · Proyecto Diamante</h1>
            <p className="text-neutral-400 text-sm mt-3">Bienvenido {user?.name}. Rol activo: <span className="text-[#FFBF57]">{user?.role}</span></p>
          </div>

          <div className="flex flex-wrap gap-3 text-xs">
            <TabBtn active={tab === "pipeline"} onClick={() => setTab("pipeline")} testId={CRM.tabPipeline} icon={<Users size={14} strokeWidth={1.5} />} label="Pipeline" />
            <TabBtn active={tab === "locations"} onClick={() => setTab("locations")} testId={CRM.tabLocations} icon={<MapPin size={14} strokeWidth={1.5} />} label="Sucursales" />
            {canAdmin && (
              <>
                <TabBtn active={tab === "cms"} onClick={() => setTab("cms")} testId="tab-cms" icon={<Palette size={14} strokeWidth={1.5} />} label="Contenido" />
                <TabBtn active={tab === "rubipos"} onClick={() => setTab("rubipos")} testId="tab-rubipos" icon={<Store size={14} strokeWidth={1.5} />} label="RubiPOS" />
                <TabBtn active={tab === "investors"} onClick={() => setTab("investors")} testId="tab-investors" icon={<Briefcase size={14} strokeWidth={1.5} />} label="Inversionistas" />
                <TabBtn active={tab === "users"} onClick={() => setTab("users")} testId={CRM.tabUsers} icon={<ShieldCheck size={14} strokeWidth={1.5} />} label="Usuarios" />
                <TabBtn active={tab === "audit"} onClick={() => setTab("audit")} testId={CRM.tabAudit} icon={<ClipboardList size={14} strokeWidth={1.5} />} label="Auditoría" />
                {user?.role === "admin" && <TabBtn active={tab === "settings"} onClick={() => setTab("settings")} testId="tab-settings" icon={<Settings size={14} strokeWidth={1.5} />} label="Configuración" />}
              </>
            )}
          </div>
        </div>

        {/* Summary cards */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            <StatCard icon={<Users size={16} strokeWidth={1.5} />} label="Total leads" value={summary.total} testId={CRM.summaryTotal} />
            <StatCard icon={<Wallet size={16} strokeWidth={1.5} />} label="Capital potencial" value={CAPITAL_FMT.format(summary.estimated_capital)} testId={CRM.summaryCapital} />
            <StatCard icon={<TrendingUp size={16} strokeWidth={1.5} />} label="Conversión" value={`${summary.conversion_rate}%`} testId={CRM.summaryConversion} />
            <StatCard icon={<ShieldCheck size={16} strokeWidth={1.5} />} label="Calificados" value={summary.by_label?.calificado || 0} />
          </div>
        )}

        {tab === "pipeline" && (
          <>
            <div className="flex flex-wrap gap-2 mb-6 text-xs">
              {[
                ["all", "Todos"],
                ["calificado", "Calificados"],
                ["a_revisar", "A revisar"],
                ["no_calificado", "No calificados"],
              ].map(([v, l]) => (
                <button
                  key={v}
                  onClick={() => setFilterLabel(v)}
                  className={`px-3 py-1.5 border text-[11px] uppercase tracking-widest transition-colors ${filterLabel === v ? "border-[#FFBF57] text-[#FFBF57]" : "border-white/10 text-neutral-400 hover:text-white"}`}
                >
                  {l}
                </button>
              ))}
            </div>

            <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${STAGES.length}, minmax(240px, 1fr))`, overflowX: "auto" }}>
              {STAGES.map((stage) => (
                <div key={stage} data-testid={CRM.pipelineColumn(stage)} className="bg-[#111112] border border-white/10 min-h-[400px]">
                  <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                    <span className="text-[11px] uppercase tracking-widest text-neutral-400">{stage}</span>
                    <span className="text-xs text-[#FFBF57]">{grouped[stage]?.length || 0}</span>
                  </div>
                  <div className="p-3 flex flex-col gap-3">
                    {(grouped[stage] || []).map((lead) => (
                      <LeadCard key={lead.lead_id} lead={lead} />
                    ))}
                    {(!grouped[stage] || grouped[stage].length === 0) && (
                      <div className="text-xs text-neutral-600 italic px-2 py-6 text-center">Sin leads</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "locations" && (
          <LocationsPanel locations={locations} canEdit={canAdmin} onUpdate={loadLocations} />
        )}
        {tab === "cms" && canAdmin && (
          <CmsPanel />
        )}
        {tab === "rubipos" && canAdmin && <RubiposPanel onOpenIntegrations={() => setTab("settings")} />}
        {tab === "investors" && canAdmin && <InvestorsPanel />}
        {tab === "settings" && user?.role === "admin" && <IntegrationsPanel />}
        {tab === "users" && canAdmin && (
          <UsersPanel users={users} me={user} onUpdate={loadUsers} />
        )}
        {tab === "audit" && canAdmin && (
          <AuditPanel audit={audit} />
        )}
      </section>
    </div>
  );
}

function LeadCard({ lead }) {
  const navigate = useNavigate();
  const s = LABEL_STYLE[lead.score_label] || LABEL_STYLE.no_calificado;
  return (
    <button
      data-testid={CRM.leadCard(lead.lead_id)}
      onClick={() => navigate(`/dashboard/leads/${lead.lead_id}`)}
      className="text-left border border-white/10 hover:border-[#FFBF57]/50 bg-[#0A0A0A] p-3 transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="font-display text-base leading-tight">{lead.name}</div>
        <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${s}`}>{lead.score}</span>
      </div>
      <div className="text-[11px] text-neutral-500 mt-1">{lead.city}</div>
      <div className="text-[11px] text-neutral-400 mt-3 flex gap-2 items-center">
        <span className="text-[#FFBF57]">{lead.capital_estimated}</span>
        <span className="text-neutral-600">·</span>
        <span>{lead.timing}</span>
      </div>
    </button>
  );
}

function StatCard({ icon, label, value, testId }) {
  return (
    <div data-testid={testId} className="border border-white/10 bg-[#111112] p-5">
      <div className="flex items-center gap-2 text-neutral-500 text-[11px] uppercase tracking-widest">
        <span className="text-[#FFBF57]">{icon}</span>
        {label}
      </div>
      <div className="font-display text-3xl mt-3">{value}</div>
    </div>
  );
}

function TabBtn({ active, onClick, icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-4 py-2 border flex items-center gap-2 uppercase tracking-widest text-[11px] transition-colors ${active ? "border-[#FFBF57] text-[#FFBF57] bg-[#FFBF57]/[0.05]" : "border-white/10 text-neutral-400 hover:border-white/25 hover:text-white"}`}
    >
      {icon} {label}
    </button>
  );
}

function LocationsPanel({ locations, canEdit, onUpdate }) {
  const change = async (id, status) => {
    try {
      await api.patch(`/locations/${id}`, { status });
      toast.success("Estatus actualizado");
      onUpdate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    }
  };
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {locations.map((loc) => {
        const s = LOC_STATUS[loc.status] || LOC_STATUS.prospectada;
        return (
          <div key={loc.location_id} className="border border-white/10 bg-[#111112] p-6">
            <div className="font-display text-2xl">{loc.name}</div>
            <div className="text-neutral-400 text-sm">{loc.city}</div>
            <div className={`inline-block mt-4 px-3 py-1 border text-[10px] uppercase tracking-widest ${s.cls}`}>{s.label}</div>
            <p className="text-neutral-500 text-xs mt-4 leading-relaxed">{loc.notes}</p>

            {canEdit && (
              <div className="mt-6 flex flex-wrap gap-2 border-t border-white/5 pt-4">
                {Object.entries(LOC_STATUS).map(([v, s2]) => (
                  <button
                    key={v}
                    onClick={() => change(loc.location_id, v)}
                    className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${loc.status === v ? s2.cls : "border-white/10 text-neutral-500 hover:text-white"}`}
                    data-testid={`loc-${loc.location_id}-${v}`}
                  >
                    {s2.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function UsersPanel({ users, me, onUpdate }) {
  const [showInvite, setShowInvite] = useState(false);
  const [showProspect, setShowProspect] = useState(false);
  const [docsFor, setDocsFor] = useState(null); // {user_id, name, email}
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "ventas" });
  const [prospectForm, setProspectForm] = useState({ email: "", name: "", phone: "", project_code: "" });

  const setRole = async (uid, role) => {
    try { await api.patch(`/users/${uid}/role`, { role }); toast.success("Rol actualizado"); onUpdate(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo actualizar"); }
  };

  const remove = async (uid, email) => {
    if (!window.confirm(`¿Eliminar al usuario ${email}?`)) return;
    try { await api.delete(`/users/${uid}`); toast.success("Usuario eliminado"); onUpdate(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo eliminar"); }
  };

  const promote = async (uid, email) => {
    if (!window.confirm(`¿Promover ${email} a Inversionista? Esta acción da acceso a la intranet de rendimiento.`)) return;
    try { await api.patch(`/users/${uid}/promote-investor`); toast.success("Promovido a Inversionista"); onUpdate(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo promover"); }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name) { toast.error("Completa nombre y correo"); return; }
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success(`Usuario ${form.email} creado. Iniciará sesión con Google.`);
      setForm({ email: "", name: "", role: "ventas" }); setShowInvite(false); onUpdate();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo crear"); }
    finally { setBusy(false); }
  };

  const createProspect = async (e) => {
    e.preventDefault();
    if (!prospectForm.email || !prospectForm.name) { toast.error("Completa nombre y correo"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/users/prospect", prospectForm);
      toast.success(`Cuenta creada. Contraseña inicial: ${data.temp_password}. Comparte con el interesado.`);
      setProspectForm({ email: "", name: "", phone: "", project_code: "" }); setShowProspect(false); onUpdate();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo crear la cuenta"); }
    finally { setBusy(false); }
  };

  const isAdmin = me?.role === "admin";
  const isVentas = ["admin", "director", "ventas"].includes(me?.role);
  const ROLES = ["admin", "director", "ventas", "marketing", "interesado", "inversionista"];
  return (
    <div>
      {isVentas && (
        <div className="mb-4 flex items-center justify-between border border-white/10 bg-[#111112] rounded-2xl p-4 gap-3 flex-wrap">
          <div>
            <div className="text-sm text-[#F5F5F0] font-medium">Equipo + prospectos · {users.length}</div>
            <div className="text-xs text-neutral-500 mt-0.5">Miembros del equipo (Google) e interesados/inversionistas (correo + contraseña).</div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowProspect(true)} className="bg-white/10 hover:bg-white/15 text-white text-xs uppercase tracking-widest font-semibold px-4 py-2 rounded-full transition-colors" data-testid="btn-create-prospect">
              + Cuenta prospecto
            </button>
            {isAdmin && (
              <button onClick={() => setShowInvite(true)} className="bg-[#FFBF57] hover:bg-[#FFCF7F] text-black text-xs uppercase tracking-widest font-semibold px-4 py-2 rounded-full transition-colors" data-testid="btn-invite-user">
                + Invitar equipo
              </button>
            )}
          </div>
        </div>
      )}

      <div className="border border-white/10 overflow-hidden rounded-2xl">
        <table className="w-full text-sm">
          <thead className="bg-[#111112] text-[10px] uppercase tracking-widest text-neutral-500">
            <tr>
              <th className="text-left px-4 py-3">Nombre</th>
              <th className="text-left px-4 py-3">Correo</th>
              <th className="text-left px-4 py-3">Rol</th>
              {isAdmin && <th className="text-left px-4 py-3">Acción</th>}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isProspect = u.role === "interesado";
              return (
                <tr key={u.user_id} className="border-t border-white/10">
                  <td className="px-4 py-3 font-display text-base">{u.name}</td>
                  <td className="px-4 py-3 text-neutral-400">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-widest border px-2 py-1 ${u.role === "inversionista" ? "text-emerald-400 border-emerald-500/40" : "text-[#FFBF57] border-[#FFBF57]/30"}`}>{u.role}</span>
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3 flex items-center gap-2 flex-wrap">
                      <select value={u.role} onChange={(e) => setRole(u.user_id, e.target.value)} className="bg-transparent border border-white/10 text-xs px-2 py-1 focus:border-[#FFBF57] outline-none" data-testid={`user-role-${u.user_id}`}>
                        {ROLES.map((r) => <option key={r} value={r} className="bg-[#111]">{r}</option>)}
                      </select>
                      {isProspect && (
                        <button onClick={() => setDocsFor({ user_id: u.user_id, name: u.name, email: u.email })} className="text-xs text-[#FFBF57] hover:text-white px-2 py-1 border border-[#FFBF57]/40 rounded" data-testid={`user-docs-${u.user_id}`}>
                          Documentos
                        </button>
                      )}
                      {isProspect && (
                        <button onClick={() => promote(u.user_id, u.email)} className="text-xs text-emerald-400 hover:text-emerald-300 px-2 py-1 border border-emerald-500/30 rounded" data-testid={`user-promote-${u.user_id}`}>
                          → Inversionista
                        </button>
                      )}
                      {u.user_id !== me.user_id && (
                        <button onClick={() => remove(u.user_id, u.email)} className="text-xs text-red-400 hover:text-red-300 px-2 py-1 border border-red-500/30 rounded" data-testid={`user-delete-${u.user_id}`}>
                          Eliminar
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Invite staff modal (Google) */}
      {showInvite && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setShowInvite(false)} data-testid="invite-user-modal">
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="bg-[#0F0F13] border border-white/10 rounded-2xl p-8 w-full max-w-md">
            <div className="text-xs uppercase tracking-widest text-[#FFBF57] font-mono mb-2">Invitar miembro del equipo</div>
            <h3 className="font-display text-2xl text-white mb-6">Nuevo usuario · Google</h3>
            <div className="space-y-4">
              <Input label="Nombre completo" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="invite-name" />
              <Input label="Correo Google del usuario" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="invite-email" hint="Iniciará sesión con Google." />
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono mb-1.5 block">Rol</span>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full bg-[#111] border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white focus:border-[#FFBF57] outline-none" data-testid="invite-role">
                  <option value="ventas">Ventas</option>
                  <option value="marketing">Marketing</option>
                  <option value="director">Director</option>
                  <option value="admin">Admin</option>
                </select>
              </label>
            </div>
            <ModalActions onCancel={() => setShowInvite(false)} busy={busy} submitTestId="invite-submit" submitLabel="Crear usuario" />
          </form>
        </div>
      )}

      {/* Create prospect modal (email + password) */}
      {showProspect && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setShowProspect(false)} data-testid="prospect-modal">
          <form onClick={(e) => e.stopPropagation()} onSubmit={createProspect} className="bg-[#0F0F13] border border-white/10 rounded-2xl p-8 w-full max-w-md">
            <div className="text-xs uppercase tracking-widest text-[#FFBF57] font-mono mb-2">Cuenta de interesado</div>
            <h3 className="font-display text-2xl text-white mb-2">Nueva cuenta prospecto</h3>
            <p className="text-xs text-neutral-500 mb-6">Contraseña inicial automática: <span className="text-[#FFBF57] font-mono">expansion2026</span>. El interesado la usa junto con su correo para acceder al portal.</p>
            <div className="space-y-4">
              <Input label="Nombre completo" value={prospectForm.name} onChange={(v) => setProspectForm({ ...prospectForm, name: v })} testId="prospect-name" />
              <Input label="Correo del interesado (cualquier proveedor)" type="email" value={prospectForm.email} onChange={(v) => setProspectForm({ ...prospectForm, email: v })} testId="prospect-email" />
              <Input label="Teléfono (opcional)" value={prospectForm.phone} onChange={(v) => setProspectForm({ ...prospectForm, phone: v })} testId="prospect-phone" />
              <Input label="Proyecto de interés (código, opcional)" value={prospectForm.project_code} onChange={(v) => setProspectForm({ ...prospectForm, project_code: v })} testId="prospect-project" />
            </div>
            <ModalActions onCancel={() => setShowProspect(false)} busy={busy} submitTestId="prospect-submit" submitLabel="Crear cuenta" />
          </form>
        </div>
      )}

      {/* Prospect documents modal */}
      {docsFor && (
        <ProspectDocsModal prospect={docsFor} onClose={() => setDocsFor(null)} />
      )}
    </div>
  );
}

function ProspectDocsModal({ prospect, onClose }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: "", category: "presentacion", note: "" });

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/users/${prospect.user_id}/documents`);
      setDocs(data);
    } catch (e) {
      toast.error("No se pudieron cargar los documentos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [prospect.user_id]); // eslint-disable-line

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!form.title) { toast.error("Escribe primero el título del documento"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: uploaded } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/users/${prospect.user_id}/documents`, {
        file_id: uploaded.file_id,
        title: form.title,
        category: form.category,
        note: form.note || undefined,
      });
      toast.success("Documento asignado");
      setForm({ title: "", category: "presentacion", note: "" });
      e.target.value = "";
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al subir");
    } finally {
      setUploading(false);
    }
  };

  const removeDoc = async (doc_id) => {
    if (!window.confirm("¿Eliminar este documento del prospecto?")) return;
    try {
      await api.delete(`/users/${prospect.user_id}/documents/${doc_id}`);
      toast.success("Eliminado");
      reload();
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose} data-testid="prospect-docs-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0F0F13] border border-white/10 rounded-2xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="text-xs uppercase tracking-widest text-[#FFBF57] font-mono mb-2">Documentos del interesado</div>
        <h3 className="font-display text-2xl text-white">{prospect.name}</h3>
        <p className="text-xs text-neutral-500 mt-1">{prospect.email}</p>

        {/* Upload form */}
        <div className="mt-6 p-4 border border-white/10 rounded-xl">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono mb-3">Asignar nuevo documento</div>
          <div className="grid md:grid-cols-2 gap-3">
            <Input label="Título" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testId="doc-title" />
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono mb-1.5 block">Categoría</span>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-[#111] border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white focus:border-[#FFBF57] outline-none" data-testid="doc-category">
                <option value="presentacion">Presentación</option>
                <option value="contrato">Contrato</option>
                <option value="modelo">Modelo financiero</option>
                <option value="documento">Otro documento</option>
              </select>
            </label>
          </div>
          <label className="block mt-3">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono mb-1.5 block">Nota (opcional)</span>
            <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="w-full bg-transparent border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white focus:border-[#FFBF57] outline-none" data-testid="doc-note" />
          </label>
          <label className="mt-3 block">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono mb-1.5 block">Archivo (PDF, imagen, video)</span>
            <input type="file" onChange={onFileChange} disabled={uploading || !form.title} className="block w-full text-xs text-neutral-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-[#FFBF57] file:text-black hover:file:bg-[#FFCF7F] disabled:opacity-50 cursor-pointer" data-testid="doc-file" />
          </label>
          {uploading && <div className="text-xs text-[#FFBF57] mt-2 font-mono">Subiendo…</div>}
        </div>

        {/* List */}
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono mb-3">Documentos asignados ({docs.length})</div>
          {loading ? (
            <div className="text-xs text-neutral-500 font-mono">Cargando…</div>
          ) : docs.length === 0 ? (
            <div className="text-xs text-neutral-500 italic">Aún no hay documentos asignados a este prospecto.</div>
          ) : (
            <div className="space-y-2">
              {docs.map((d) => (
                <div key={d.doc_id} className="flex items-center justify-between p-3 bg-black/30 border border-white/5 rounded-lg" data-testid={`doc-row-${d.doc_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-white truncate">{d.title}</div>
                    <div className="text-[10px] font-mono text-neutral-500 mt-0.5">{d.category} · {d.content_type}</div>
                    {d.note && <div className="text-[11px] text-neutral-400 mt-1">{d.note}</div>}
                  </div>
                  <div className="flex gap-2 ml-3">
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/public/files/${d.file_id}`} target="_blank" rel="noreferrer" className="text-xs text-neutral-300 hover:text-[#FFBF57] px-2 py-1 border border-white/10 rounded">Abrir</a>
                    <button onClick={() => removeDoc(d.doc_id)} className="text-xs text-red-400 hover:text-red-300 px-2 py-1 border border-red-500/30 rounded" data-testid={`doc-delete-${d.doc_id}`}>Eliminar</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="text-xs uppercase tracking-widest text-neutral-400 hover:text-white px-4 py-2">Cerrar</button>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testId, hint }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono mb-1.5 block">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white focus:border-[#FFBF57] outline-none" data-testid={testId} required />
      {hint && <div className="text-[10px] text-neutral-500 mt-1.5">{hint}</div>}
    </div>
  );
}

function ModalActions({ onCancel, busy, submitTestId, submitLabel }) {
  return (
    <div className="mt-8 flex items-center justify-end gap-3">
      <button type="button" onClick={onCancel} className="text-xs uppercase tracking-widest text-neutral-400 hover:text-white px-4 py-2">Cancelar</button>
      <button type="submit" disabled={busy} className="bg-[#FFBF57] hover:bg-[#FFCF7F] text-black text-xs uppercase tracking-widest font-semibold px-5 py-2.5 rounded-full disabled:opacity-50" data-testid={submitTestId}>
        {busy ? "Creando…" : submitLabel}
      </button>
    </div>
  );
}

function AuditPanel({ audit }) {
  return (
    <div className="border border-white/10">
      <table className="w-full text-sm">
        <thead className="bg-[#111112] text-[10px] uppercase tracking-widest text-neutral-500">
          <tr>
            <th className="text-left px-4 py-3">Fecha</th>
            <th className="text-left px-4 py-3">Usuario</th>
            <th className="text-left px-4 py-3">Acción</th>
            <th className="text-left px-4 py-3">Entidad</th>
            <th className="text-left px-4 py-3">Detalles</th>
          </tr>
        </thead>
        <tbody>
          {audit.map((a) => (
            <tr key={a.log_id} className="border-t border-white/5">
              <td className="px-4 py-3 text-neutral-500 text-xs">{new Date(a.timestamp).toLocaleString("es-MX")}</td>
              <td className="px-4 py-3 text-neutral-300 text-xs">{a.user_email || "sistema"}</td>
              <td className="px-4 py-3 text-[#FFBF57] text-xs">{a.action}</td>
              <td className="px-4 py-3 text-neutral-400 text-xs">{a.entity}</td>
              <td className="px-4 py-3 text-neutral-500 text-[11px] font-mono">{JSON.stringify(a.changes)}</td>
            </tr>
          ))}
          {audit.length === 0 && (
            <tr><td colSpan={5} className="px-4 py-8 text-center text-neutral-600 text-sm">Sin registros</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
